/**
 * Extensible payment instructions for invoices.
 *
 * Storage shape (org settings + invoice snapshot) is separate from the
 * presentation model consumed by Preview/PDF renderers.
 *
 * Each payment method owns its editor metadata and presentation blocks.
 * Invoice renderers only consume the normalized presentation object and
 * stay payment-method agnostic. Future methods (Clover, Stripe, Square,
 * ACH/EFT, QR, …) register here without changing Preview/PDF layout code.
 */

import { supabase } from "../supabase";
import { getEffectiveOrgSetting } from "./organizationSettings";
import { loadActorOrganizationSettings } from "./invoiceOrgSettings";

export const PAYMENT_METHOD = Object.freeze({
  INTERAC_ETRANSFER: "interac_etransfer",
  BANK_TRANSFER: "bank_transfer",
  CHEQUE: "cheque",
  CUSTOM: "custom",
  // Reserved for future method adapters:
  // CLOVER: "clover",
  // STRIPE: "stripe",
  // SQUARE: "square",
  // ACH: "ach",
  // EFT: "eft",
  // QR: "qr",
});

/** Customer-facing section title on Review / Preview / PDF / Summary. */
export const PAYMENT_INSTRUCTIONS_CUSTOMER_TITLE = "How to Pay";

/** Internal Admin section title. */
export const PAYMENT_INSTRUCTIONS_ADMIN_TITLE = "Payment Instructions";

export const EMPTY_PAYMENT_INSTRUCTIONS = Object.freeze({
  preferredMethod: "",
  paymentEmail: "",
  additionalInstructions: "",
  customMethodLabel: "",
});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function nonEmptyText(value) {
  const text = String(value || "").trim();
  return text || "";
}

function preferredMethodBlock(label) {
  return {
    type: "field",
    label: "Preferred Method",
    value: label,
  };
}

/**
 * Registry: each method owns dropdown label, editor fields, and presentation.
 * Renderers must not branch on method IDs — only consume `blocks`.
 */
export const PAYMENT_METHOD_REGISTRY = Object.freeze({
  [PAYMENT_METHOD.INTERAC_ETRANSFER]: Object.freeze({
    id: PAYMENT_METHOD.INTERAC_ETRANSFER,
    label: "Interac e-Transfer",
    editorFields: Object.freeze([
      Object.freeze({
        key: "paymentEmail",
        label: "Interac e-Transfer Email",
        kind: "email",
        placeholder: "payments@example.com",
        required: true,
      }),
    ]),
    showAdditionalInstructions: true,
    additionalInstructionsLabel: "Additional payment instructions",
    buildPresentationBlocks(config) {
      const blocks = [preferredMethodBlock("Interac e-Transfer")];
      if (config.paymentEmail) {
        blocks.push({
          type: "field",
          label: "Send payment to",
          value: config.paymentEmail,
        });
      }
      blocks.push({
        type: "text",
        text: "Please include the invoice number in the transfer message.",
      });
      return blocks;
    },
  }),

  [PAYMENT_METHOD.BANK_TRANSFER]: Object.freeze({
    id: PAYMENT_METHOD.BANK_TRANSFER,
    label: "Bank Transfer",
    editorFields: Object.freeze([
      Object.freeze({
        key: "paymentEmail",
        label: "Bank Account Details",
        kind: "textarea",
        placeholder:
          "Institution name, transit #, account #, and any remittance details",
        required: false,
      }),
    ]),
    showAdditionalInstructions: true,
    additionalInstructionsLabel: "Additional payment instructions",
    buildPresentationBlocks(config) {
      const blocks = [preferredMethodBlock("Bank Transfer")];
      if (config.paymentEmail) {
        blocks.push({
          type: "field",
          label: "Bank Account Details",
          value: config.paymentEmail,
        });
      }
      return blocks;
    },
  }),

  [PAYMENT_METHOD.CHEQUE]: Object.freeze({
    id: PAYMENT_METHOD.CHEQUE,
    label: "Cheque",
    editorFields: Object.freeze([
      Object.freeze({
        key: "paymentEmail",
        label: "Cheque Payable To",
        kind: "text",
        placeholder: "Payee name as it should appear on the cheque",
        required: false,
      }),
    ]),
    showAdditionalInstructions: true,
    additionalInstructionsLabel: "Additional payment instructions",
    buildPresentationBlocks(config) {
      const blocks = [preferredMethodBlock("Cheque")];
      if (config.paymentEmail) {
        blocks.push({
          type: "field",
          label: "Cheque Payable To",
          value: config.paymentEmail,
        });
      }
      return blocks;
    },
  }),

  [PAYMENT_METHOD.CUSTOM]: Object.freeze({
    id: PAYMENT_METHOD.CUSTOM,
    label: "Custom",
    editorFields: Object.freeze([
      Object.freeze({
        key: "customMethodLabel",
        label: "Custom method label",
        kind: "text",
        placeholder: "e.g. Wire transfer via accounting",
        required: false,
      }),
      Object.freeze({
        key: "additionalInstructions",
        label: "Custom Payment Instructions",
        kind: "textarea",
        placeholder: "Describe how the customer should pay",
        required: false,
      }),
    ]),
    showAdditionalInstructions: false,
    additionalInstructionsLabel: "Custom Payment Instructions",
    buildPresentationBlocks(config) {
      const label = config.customMethodLabel || "Custom";
      const blocks = [preferredMethodBlock(label)];
      if (config.paymentEmail) {
        blocks.push({
          type: "field",
          label: "Send payment to",
          value: config.paymentEmail,
        });
      }
      return blocks;
    },
  }),
});

export const PAYMENT_METHOD_OPTIONS = Object.freeze(
  Object.values(PAYMENT_METHOD_REGISTRY).map((method) => ({
    value: method.id,
    label: method.label,
  })),
);

export function getPaymentMethodDefinition(methodId) {
  return PAYMENT_METHOD_REGISTRY[methodId] || null;
}

export function getPaymentMethodOption(methodId) {
  return (
    PAYMENT_METHOD_OPTIONS.find((option) => option.value === methodId) || null
  );
}

export function normalizePaymentInstructions(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...EMPTY_PAYMENT_INSTRUCTIONS };
  }

  const preferredMethod = nonEmptyText(raw.preferredMethod);

  return {
    preferredMethod,
    paymentEmail: nonEmptyText(raw.paymentEmail),
    additionalInstructions: nonEmptyText(raw.additionalInstructions),
    customMethodLabel: nonEmptyText(raw.customMethodLabel),
  };
}

export function hasConfiguredPaymentInstructions(raw) {
  const config = normalizePaymentInstructions(raw);
  return Boolean(
    config.preferredMethod ||
      config.paymentEmail ||
      config.additionalInstructions ||
      config.customMethodLabel,
  );
}

/**
 * Persistable snapshot. Returns null when nothing is configured so historical
 * invoices without payment instructions stay empty / omitted.
 */
export function serializePaymentInstructions(raw) {
  const config = normalizePaymentInstructions(raw);
  if (!hasConfiguredPaymentInstructions(config)) {
    return null;
  }
  return config;
}

export function resolvePreferredMethodLabel(config) {
  const normalized = normalizePaymentInstructions(config);
  if (!normalized.preferredMethod) {
    return "";
  }
  const definition = getPaymentMethodDefinition(normalized.preferredMethod);
  if (normalized.preferredMethod === PAYMENT_METHOD.CUSTOM) {
    return normalized.customMethodLabel || definition?.label || "Custom";
  }
  return (
    definition?.label ||
    normalized.customMethodLabel ||
    normalized.preferredMethod
  );
}

/**
 * Build the structured presentation object consumed by Preview/PDF.
 * Returns null when there is nothing to render (backward compatible omit).
 */
export function buildPaymentInstructionsPresentation(raw, options = {}) {
  const config = normalizePaymentInstructions(raw);
  if (!hasConfiguredPaymentInstructions(config)) {
    return null;
  }

  const definition =
    getPaymentMethodDefinition(config.preferredMethod) ||
    PAYMENT_METHOD_REGISTRY[PAYMENT_METHOD.CUSTOM];

  const blocks = definition?.buildPresentationBlocks
    ? [...definition.buildPresentationBlocks(config)]
    : [];

  // Org/invoice optional notes append after method-owned blocks, except when
  // Custom already treats additionalInstructions as its primary body field
  // (still append once via the Custom field path below for empty method blocks).
  if (
    config.additionalInstructions &&
    definition?.showAdditionalInstructions !== false
  ) {
    blocks.push({
      type: "text",
      text: config.additionalInstructions,
    });
  } else if (
    config.additionalInstructions &&
    config.preferredMethod === PAYMENT_METHOD.CUSTOM
  ) {
    blocks.push({
      type: "text",
      text: config.additionalInstructions,
    });
  }

  if (blocks.length === 0) {
    return null;
  }

  return {
    title: options.title || PAYMENT_INSTRUCTIONS_CUSTOMER_TITLE,
    methodId: config.preferredMethod || null,
    config,
    blocks,
  };
}

export function validatePaymentInstructions(
  raw,
  { requireConfigured = false } = {},
) {
  const config = normalizePaymentInstructions(raw);
  const errors = {};
  const definition = getPaymentMethodDefinition(config.preferredMethod);

  if (requireConfigured && !hasConfiguredPaymentInstructions(config)) {
    errors.preferredMethod = "Select a preferred payment method.";
  }

  if (
    config.preferredMethod === PAYMENT_METHOD.INTERAC_ETRANSFER &&
    !config.paymentEmail
  ) {
    errors.paymentEmail = "Interac e-Transfer email is required.";
  }

  // Email format only when the active method's primary field is an email.
  const emailField = definition?.editorFields?.find(
    (field) => field.key === "paymentEmail" && field.kind === "email",
  );
  if (
    emailField &&
    config.paymentEmail &&
    !EMAIL_PATTERN.test(config.paymentEmail)
  ) {
    errors.paymentEmail = "Enter a valid payment email address.";
  }

  if (
    config.preferredMethod === PAYMENT_METHOD.CUSTOM &&
    !config.customMethodLabel &&
    !config.additionalInstructions
  ) {
    errors.customMethodLabel =
      "Enter a custom method label or custom payment instructions.";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    config,
  };
}

/**
 * Resolve effective instructions for an invoice draft:
 * org defaults unless the invoice overrides them.
 */
export function resolveInvoicePaymentInstructions({
  invoice = null,
  organizationPaymentInstructions = null,
} = {}) {
  const override = invoice?.payment_instructions_override === true;
  if (override) {
    return normalizePaymentInstructions(invoice?.payment_instructions);
  }

  if (hasConfiguredPaymentInstructions(organizationPaymentInstructions)) {
    return normalizePaymentInstructions(organizationPaymentInstructions);
  }

  // Legacy / stamped invoices without an override flag still use stored snapshot.
  if (hasConfiguredPaymentInstructions(invoice?.payment_instructions)) {
    return normalizePaymentInstructions(invoice.payment_instructions);
  }

  return { ...EMPTY_PAYMENT_INSTRUCTIONS };
}

export function getOrganizationPaymentInstructions(settings) {
  return normalizePaymentInstructions(
    getEffectiveOrgSetting(settings, "invoices.payment_instructions", null),
  );
}

export async function loadDefaultPaymentInstructions(
  actor,
  client = supabase,
) {
  try {
    const settings = await loadActorOrganizationSettings(actor, client);
    return getOrganizationPaymentInstructions(settings);
  } catch (err) {
    console.warn("[invoicePaymentInstructions] default fallback", err);
    return { ...EMPTY_PAYMENT_INSTRUCTIONS };
  }
}
